/**
 * Мониторинг (Remetry) — общая часть native- и web-обёрток.
 *
 * Здесь всё, что не зависит от платформы: конфиг из expo-constants, состояние
 * (enabled/userId), сборка и отправка событий. Платформенные файлы
 * (monitoring.ts / monitoring.web.ts) держат свой плагин глобальных хендлеров,
 * свой init() и «стамп» — поля события, которые считаются по-своему на каждой
 * платформе (native: os; web: url/browser).
 *
 * Всё шлём через captureEvent, а не captureError: последний не проставляет
 * userId, а он нужен в каждом событии.
 *
 * flush наружу не экспортируем: единственные точки принудительного флаша —
 * pagehide (web) и fatal (native), обе внутри плагинов; понадобится хосту —
 * вернуть экспорт поверх flush из '@remetry/browser'.
 *
 * Fail-safe: тело каждой экспортируемой функции обёрнуто в try/catch —
 * мониторинг никогда не должен ронять приложение.
 */
import Constants from 'expo-constants'
import { captureEvent } from '@remetry/browser'

const extra = Constants.expoConfig?.extra ?? {}
export const dsn = (extra.remetryDsn as string) ?? ''
export const endpoint = (extra.remetryEndpoint as string) ?? ''
export const release = (extra.remetryRelease as string) ?? ''

/** SDK не экспортирует тип события — берём его из сигнатуры captureEvent. */
type RemetryEvent = Parameters<typeof captureEvent>[0]

/** Платформенные поля события; платформенные guard-ы живут внутри стампа. */
type Stamp = (event: Omit<RemetryEvent, 'timestamp'>) => Partial<RemetryEvent>

let enabled = false
let userId: string | undefined
let platformStamp: Stamp = () => ({})

/**
 * Включает отправку и запоминает платформенный стамп. false — инициализировать
 * нечего: либо уже включено (идемпотентность), либо kill-switch (пустой
 * dsn/endpoint), и тогда SDK не инициализируется вовсе.
 *
 * enabled выставляется ДО init() и не сбрасывается, если тот бросит: повторный
 * initMonitoring не должен переустановить глобальные хендлеры.
 */
export function armMonitoring(stamp: Stamp): boolean {
  if (enabled || !dsn || !endpoint) return false
  enabled = true
  platformStamp = stamp
  return true
}

/** timestamp/release/sessionId/eventId: время ставим сами, остальное доштампует SDK. */
function send(event: Omit<RemetryEvent, 'timestamp'>): void {
  if (!enabled) return
  captureEvent({
    ...event,
    timestamp: new Date().toISOString(),
    userId,
    ...platformStamp(event),
  })
}

function stringify(v: unknown): string {
  try {
    return JSON.stringify(v) ?? String(v)
  } catch {
    return String(v)
  }
}

/** В хендлеры прилетает что угодно, а не только Error. */
function errorPayload(err: unknown, componentStack?: string) {
  if (err instanceof Error) {
    return { name: err.name, message: err.message, stack: err.stack, componentStack }
  }
  if (typeof err === 'string') return { name: 'Error', message: err, componentStack }
  if (err == null) return { name: 'Error', message: 'Unknown error', componentStack }
  return { name: 'Error', message: stringify(err), componentStack }
}

/**
 * Контекст события. `source` — откуда прилетела ошибка («query edo-tasks»):
 * едет ПРЕФИКСОМ message, а не отдельным полем, потому что (1) контракт события
 * фиксирован (ErrorPayload в @remetry/browser — name/message/stack/componentStack),
 * лишние поля бэк не показывает, и (2) issue группируется по
 * name|нормализованный message|верхний фрейм стека — значит только через message
 * источник разводит отказы разных ручек по разным issue, а не схлопывает их в
 * один «ApiError: 500 Internal Server Error».
 */
type ErrorCtx = { componentStack?: string; url?: string; source?: string }

/** Общий путь для reportError и глобальных хендлеров платформенных плагинов. */
export function sendError(err: unknown, ctx?: ErrorCtx): void {
  const error = errorPayload(err, ctx?.componentStack)
  if (ctx?.source) error.message = `[${ctx.source}] ${error.message}`
  send({ type: 'error', url: ctx?.url, error })
}

export function setMonitoringUser(id: string | null): void {
  try {
    userId = id ?? undefined
  } catch {
    // см. шапку модуля
  }
}

export function reportError(err: unknown, ctx?: ErrorCtx): void {
  try {
    sendError(err, ctx)
  } catch {
    // см. шапку модуля
  }
}

/**
 * 403 — не сбой (см. reportApiCall ниже), но и не должен быть невидимым: без
 * этого сигнала сломанная конфигурация ролей на бэке не видна на дашборде
 * вообще (QueryProvider.onError молчит про auth-флоу, reportApiCall дропает
 * 401/403). Отдельный путь, а не sendError/reportError — 403 не баг и не
 * должен плодить error-issues.
 *
 * ponytail: EventType SDK закрытый — "error"|"performance"|"api"|"replay"
 * (@remetry/browser/dist/index.d.ts), своего некритичного типа события SDK не
 * даёт, а лишние top-level поля бэк не показывает (см. ErrorCtx выше) — то
 * есть единственный канал, который довозит source до дашборда, это
 * error.message. Технически это всё ещё type:'error', с отдельным name, чтобы
 * не путаться с настоящими багами при разборе issues. Апгрейд, если понадобится
 * настоящее разделение: свой EventType в SDK (отдельный пакет/репо) + бэк, не
 * триажащий его как error.
 */
export function reportForbidden(source: string, url?: string): void {
  try {
    send({
      type: 'error',
      url,
      error: { name: 'Forbidden', message: `[${source}] Forbidden (403)` },
    })
  } catch {
    // см. шапку модуля
  }
}

/**
 * Репорт КАЖДОГО завершившегося API-вызова (латентность P75/P95 + error rate),
 * а не только сбоев — иначе вкладка API в дашборде никогда не наберёт данные по
 * здоровому трафику. Единственный источник api-событий: зовётся из fetchTracked
 * в api/client.ts на любой полученный ответ и на сетевое исключение (status 0).
 *
 * 401/403 — штатный auth-флоу (истёкший токен / нет прав), а не сбой: не шлём их
 * ВООБЩЕ (ни в error rate, ни в латентность), чтобы не надувать error rate
 * дашборда. Фильтр — здесь, в обёртке, а не у вызывающих, чтобы ни один
 * вызывающий не зашумел дашборд, забыв про исключение.
 */
export function reportApiCall(info: {
  method: string
  url: string
  status: number
  durationMs: number
}): void {
  try {
    if (info.status === 401 || info.status === 403) return
    send({ type: 'api', api: info })
  } catch {
    // см. шапку модуля
  }
}
